import { Injectable, NgZone } from "@angular/core";
import { NavigationEnd, NavigationExtras, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { DIGITAL_MEMORY_LOG_CONSTANTS } from "../constants/digital-memory-log.constants";

/**
 * Overview preview navigation: remember edit/list URL and restore it on back.
 */
@Injectable({
  providedIn: "root",
})
export class PreviewNavigationService {
  private pendingReturnUrl: string | null = null;
  private popStateListenerRegistered = false;
  private restoringFromBrowserBack = false;
  private readonly returnUrlKey = DIGITAL_MEMORY_LOG_CONSTANTS.SESSION_STORAGE.PREVIEW_RETURN_URL;
  private readonly inFileDetailKey = DIGITAL_MEMORY_LOG_CONSTANTS.SESSION_STORAGE.PREVIEW_IN_FILE_DETAIL;

  constructor(private router: Router, private ngZone: NgZone) {
    this.registerPopStateListener();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const path = event.urlAfterRedirects.split("?")[0];
        if (this.isOverviewPreviewPath(path)) {
          sessionStorage.removeItem(this.inFileDetailKey);
        }
      });
  }

  setReturnUrl(url?: string): void {
    const raw = url || this.router.url;
    const pathOnly = raw.split("?")[0];
    this.pendingReturnUrl = pathOnly;
    sessionStorage.setItem(this.returnUrlKey, pathOnly);
  }

  clearReturnUrl(): void {
    this.pendingReturnUrl = null;
    sessionStorage.removeItem(this.returnUrlKey);
  }

  getReturnUrl(): string | null {
    return this.pendingReturnUrl || sessionStorage.getItem(this.returnUrlKey);
  }

  markEnteringFileDetail(): void {
    sessionStorage.setItem(this.inFileDetailKey, "true");
  }

  navigateToPreview(commands: any[], extras: NavigationExtras = {}): Promise<boolean> {
    this.setReturnUrl(this.router.url);
    sessionStorage.removeItem(this.inFileDetailKey);
    return this.router.navigate(commands, { ...extras, replaceUrl: true });
  }

  backFromPreview(): boolean {
    const returnUrl = this.getReturnUrl();
    if (!returnUrl) {
      return false;
    }
    this.navigateToReturnUrl(returnUrl);
    return true;
  }

  private registerPopStateListener(): void {
    if (this.popStateListenerRegistered || typeof window === "undefined") {
      return;
    }
    this.popStateListenerRegistered = true;

    window.addEventListener(
      "popstate",
      event => {
        this.ngZone.run(() => this.handlePopState(event));
      },
      true
    );
  }

  private handlePopState(event: PopStateEvent): void {
    if (this.restoringFromBrowserBack) {
      return;
    }

    const returnUrl = this.getReturnUrl();
    if (!returnUrl) {
      return;
    }

    if (sessionStorage.getItem(this.inFileDetailKey) === "true") {
      return;
    }

    const returnPath = this.normalizePath(returnUrl.split("?")[0]);
    const currentPath = this.normalizePath(window.location.pathname);

    if (currentPath === returnPath || this.isEditOrCreatePath(currentPath)) {
      this.clearReturnUrl();
      return;
    }

    event.stopImmediatePropagation();
    this.restoringFromBrowserBack = true;
    this.navigateToReturnUrl(returnUrl).then(() => {
      this.restoringFromBrowserBack = false;
    });
  }

  private navigateToReturnUrl(returnUrl: string, attempt = 0): Promise<boolean> {
    const path = returnUrl.split("?")[0];
    const isEditOrCreate =
      path.includes("edit-digital-memory-log-") || path.includes("create-digital-memory-log-");
    const target = isEditOrCreate ? `${path}?fromPreview=true` : path;

    return this.router.navigateByUrl(target, { replaceUrl: true }).then(success => {
      if (success) {
        this.clearReturnUrl();
        return true;
      }
      if (attempt < 5) {
        return new Promise<boolean>(resolve => setTimeout(resolve, 30)).then(() =>
          this.navigateToReturnUrl(returnUrl, attempt + 1)
        );
      }
      return false;
    });
  }

  private normalizePath(path: string): string {
    if (!path) {
      return "/";
    }
    const trimmed = path.replace(/\/+$/, "");
    return trimmed || "/";
  }

  private isEditOrCreatePath(path: string): boolean {
    return path.includes("edit-digital-memory-log-") || path.includes("create-digital-memory-log-");
  }

  private isOverviewPreviewPath(path: string): boolean {
    return path.includes("/overview/") && path.includes("/preview-") && !path.includes("/file-detail-");
  }
}
